import React, { useEffect, useState } from 'react'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import { Link } from 'react-router-dom'
import authService, {
  type LoginSupportIssueType,
  type LoginSupportPayload,
  type VerificationMethod,
} from '@/services/authService'
import { validators } from '@/utils/validators'
import ValidationSummary from '@/components/common/ValidationSummary'
import logger from '@/utils/logger'
import type { ServerError } from '@/types'
import '@/styles/components/confirm-modal.css'
import '@/styles/components/auth/login-help-modal.css'

interface LoginHelpModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Pre-fill the modal's email / customer-number fields from whatever
   * the customer typed on Step 1 of the login flow. Saves them
   * retyping when they're already mid-error and reaching for help.
   * Either, both, or neither may be passed; missing values just
   * leave the field blank.
   */
  prefill?: {
    email?: string
    customer_number?: string
  }
}

const ISSUE_OPTIONS: { value: LoginSupportIssueType; label: string }[] = [
  { value: 'cannot_login', label: "I can't log in or I'm seeing an error" },
  { value: 'email_not_recognised', label: "My email isn't being recognised" },
  { value: 'forgotten_password', label: "I've forgotten my password" },
  // FEDEV-528: what actually went wrong with the code, in the customer's words.
  { value: 'code_not_received', label: 'I never received the code' },
  { value: 'code_too_late', label: 'The code arrived too late' },
  { value: 'code_did_not_work', label: "The code didn't work" },
  { value: 'wrong_contact_details', label: 'I entered the wrong email or mobile number' },
  { value: 'other', label: 'Something else' },
]

const VERIFICATION_OPTIONS: { value: VerificationMethod; label: string }[] = [
  { value: 'email', label: 'By email' },
  { value: 'sms', label: 'By text message (SMS)' },
  { value: 'both', label: 'Both email and text message' },
  { value: 'unsure', label: "I'm not sure" },
]

interface FormState {
  issue_type: LoginSupportIssueType | ''
  verification_method: VerificationMethod | ''
  name: string
  mobile: string
  email: string
  customer_number: string
  postcode: string
  house_number_or_name: string
  details: string
}

const INITIAL_STATE: FormState = {
  issue_type: '',
  verification_method: '',
  name: '',
  mobile: '',
  email: '',
  customer_number: '',
  postcode: '',
  house_number_or_name: '',
  details: '',
}

const LoginHelpModal: React.FC<LoginHelpModalProps> = ({ isOpen, onClose, prefill }) => {
  const [form, setForm] = useState<FormState>(INITIAL_STATE)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [serverErrors, setServerErrors] = useState<Record<string, string[]>>({})
  const [apiError, setApiError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // Reset form on every open, then layer the prefill on top so the
    // customer doesn't see stale values from a previous open AND
    // doesn't have to retype what they just entered on Step 1.
    setForm({
      ...INITIAL_STATE,
      email: prefill?.email ?? '',
      customer_number: prefill?.customer_number ?? '',
    })
    setFieldErrors({})
    setServerErrors({})
    setApiError('')
    setIsSubmitted(false)
    setIsSubmitting(false)
  }, [isOpen, prefill?.email, prefill?.customer_number])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        onClose()
      }
    }

    let releaseScroll: (() => void) | undefined
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      releaseScroll = lockBodyScroll()
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      releaseScroll?.()
    }
  }, [isOpen, isSubmitting, onClose])

  if (!isOpen) {
    return null
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleIssueSelect = (value: LoginSupportIssueType) => {
    setForm((prev) => ({ ...prev, issue_type: value }))
    setFieldErrors((prev) => ({ ...prev, issue_type: '' }))
  }

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && !isSubmitting) {
      onClose()
    }
  }

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}

    if (!form.issue_type) {
      errors.issue_type = 'Please choose the issue you are experiencing.'
    }

    // FEDEV-528: either will do. Demanding an email would shut out the
    // customer with only a mobile, who is the most likely to be stuck.
    if (!form.email.trim() && !form.mobile.trim()) {
      errors.email = 'Please give us an email address or a mobile number so we can get back to you.'
    } else if (form.email.trim() && !validators.isValidEmail(form.email)) {
      errors.email = 'Please enter a valid email.'
    }

    if (form.issue_type === 'cannot_login' && !form.details.trim()) {
      errors.details = 'Please tell us more about the issue.'
    }

    if (form.issue_type === 'other' && !form.details.trim()) {
      errors.details = 'Please tell us more about the issue.'
    }

    if (form.issue_type === 'email_not_recognised') {
      if (!form.postcode.trim()) {
        errors.postcode = 'Postcode is required.'
      }
      if (!form.house_number_or_name.trim()) {
        errors.house_number_or_name = 'House number or name is required.'
      }
    }

    return errors
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setApiError('')
    setServerErrors({})

    if (form.issue_type === 'forgotten_password') {
      // Password reset flow is handled by the inline link — nothing to submit.
      return
    }

    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    setIsSubmitting(true)
    try {
      const payload: LoginSupportPayload = {
        issue_type: form.issue_type as LoginSupportIssueType,
      }

      if (form.email.trim()) payload.email = form.email.trim()
      if (form.mobile.trim()) payload.mobile = form.mobile.trim()
      if (form.name.trim()) payload.name = form.name.trim()
      if (form.verification_method) payload.verification_method = form.verification_method

      if (form.customer_number.trim()) {
        payload.customer_number = form.customer_number.trim()
      }

      if (form.issue_type === 'email_not_recognised') {
        payload.postcode = form.postcode.trim()
        payload.house_number_or_name = form.house_number_or_name.trim()
      }

      if (
        form.issue_type === 'cannot_login' ||
        form.issue_type === 'other'
      ) {
        payload.details = form.details.trim()
      }

      await authService.submitLoginSupport(payload)
      setIsSubmitted(true)
    } catch (error: unknown) {
      const serverError = error as ServerError
      logger.error('Login support submission failed:', serverError)

      const body = serverError?.body
      if (body?.errors && typeof body.errors === 'object') {
        const mapped: Record<string, string[]> = {}
        Object.entries(body.errors).forEach(([field, messages]) => {
          mapped[field] = Array.isArray(messages)
            ? (messages as string[])
            : [messages as string]
        })
        setServerErrors(mapped)
      }

      setApiError(
        (body?.message as string) ||
          serverError?.message ||
          'Sorry, we could not submit your request. Please try again.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const showDetails =
    form.issue_type === 'cannot_login' || form.issue_type === 'other'
  const detailsLabel = 'Tell us more about the issue e.g. what happens when you try to login'
  const showAddressFields = form.issue_type === 'email_not_recognised'
  const showPasswordReset = form.issue_type === 'forgotten_password'

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div
        className="modal-container login-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-help-title"
      >
        <div className="modal-header">
          <h2 id="login-help-title">Having trouble logging in?</h2>
          {!isSubmitting && (
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="modal-content">
          {isSubmitted ? (
            <div className="login-help-success">
              <p>
                <strong>Thanks - we've received your request.</strong>
              </p>
              <p>
                Our team will take a look and get back to you as soon as
                possible.
              </p>
              <div className="login-help-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="login-help-intro">
                If you're having trouble accessing your account, let us know
                and our team will take a look.
              </p>

              <div className="login-help-scope-notice" role="note">
                This form goes to our technical team, not customer
                service. If your question is about deliveries, billing,
                or your order, please{' '}
                <a
                  href="https://www.mcqueensdairies.co.uk/help/"
                  target="_blank"
                  rel="noreferrer"
                >
                  contact customer service
                </a>{' '}
                instead.
              </div>

              {apiError && (
                <ValidationSummary
                  errors={serverErrors}
                  message={apiError}
                />
              )}
              {apiError && Object.keys(serverErrors).length === 0 && (
                <div className="alert alert-danger">{apiError}</div>
              )}

              <form className="auth-form" onSubmit={handleSubmit} noValidate>
                <fieldset className="login-help-issue-group">
                  <legend className="form-label">
                    What issue are you experiencing?
                  </legend>
                  {ISSUE_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`login-help-issue-option ${
                        form.issue_type === option.value ? 'is-selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="issue_type"
                        value={option.value}
                        checked={form.issue_type === option.value}
                        onChange={() => handleIssueSelect(option.value)}
                        disabled={isSubmitting}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                  {fieldErrors.issue_type && (
                    <span className="form-text is-invalid">
                      {fieldErrors.issue_type}
                    </span>
                  )}
                </fieldset>

                {form.issue_type === 'cannot_login' && (
                  <div className="login-help-tips">
                    <p className="login-help-tips-title">
                      Have you tried the following:
                    </p>
                    <ul>
                      <li>
                        Logging in via the{' '}
                        <a
                          href="https://customer.mcqueensdairies.co.uk/client/client_login.php?feature=login"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          original portal
                        </a>{' '}
                        – you may not have been moved yet
                      </li>
                      <li>
                        Reset your password using{' '}
                        <Link to="/forgot-password" onClick={onClose}>
                          Forgot password
                        </Link>
                      </li>
                    </ul>
                    <p className="login-help-tips-footer">
                      Still not working? Let us know below and we'll help get
                      you sorted.
                    </p>
                  </div>
                )}

                {!showPasswordReset && (
                  <>
                    {/* FEDEV-528: which channel they were trying tells support
                        straight away whether to look at email or SMS. */}
                    <div className="form-group">
                      <label htmlFor="login-help-verification" className="form-label">
                        How were you trying to verify your account?
                      </label>
                      <select
                        id="login-help-verification"
                        name="verification_method"
                        value={form.verification_method}
                        onChange={handleChange}
                        className="form-control"
                        disabled={isSubmitting}
                      >
                        <option value="">Please choose…</option>
                        {VERIFICATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="login-help-name" className="form-label">
                        Your name
                      </label>
                      <input
                        type="text"
                        id="login-help-name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="form-control"
                        autoComplete="name"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="login-help-email" className="form-label">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="login-help-email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="name@email.com"
                        className={`form-control ${
                          fieldErrors.email ? 'is-invalid' : ''
                        }`}
                        autoComplete="email"
                        disabled={isSubmitting}
                      />
                      {fieldErrors.email && (
                        <span className="form-text is-invalid">
                          {fieldErrors.email}
                        </span>
                      )}
                    </div>

                    {/* Either this or the email above: a customer with only a
                        mobile is the most likely to be stuck, so we must not
                        insist on an email (FEDEV-528). */}
                    <div className="form-group">
                      <label htmlFor="login-help-mobile" className="form-label">
                        Mobile number
                      </label>
                      <input
                        type="tel"
                        id="login-help-mobile"
                        name="mobile"
                        value={form.mobile}
                        onChange={handleChange}
                        placeholder="e.g. 07911 123456"
                        className={`form-control ${fieldErrors.mobile ? 'is-invalid' : ''}`}
                        autoComplete="tel"
                        disabled={isSubmitting}
                      />
                      {fieldErrors.mobile && (
                        <span className="form-text is-invalid">{fieldErrors.mobile}</span>
                      )}
                      <span className="form-text">
                        Please give us an email address or a mobile number so we can reply.
                      </span>
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="login-help-customer-number"
                        className="form-label"
                      >
                        Customer number
                      </label>
                      <input
                        type="text"
                        id="login-help-customer-number"
                        name="customer_number"
                        value={form.customer_number}
                        onChange={handleChange}
                        placeholder="Customer number (if known)"
                        className="form-control"
                        disabled={isSubmitting}
                      />
                    </div>
                  </>
                )}

                {showDetails && (
                  <div className="form-group">
                    <label htmlFor="login-help-details" className="form-label">
                      {form.issue_type === 'other'
                        ? 'Tell us more about the issue'
                        : detailsLabel}
                    </label>
                    <textarea
                      id="login-help-details"
                      name="details"
                      value={form.details}
                      onChange={handleChange}
                      rows={4}
                      className={`form-control ${
                        fieldErrors.details ? 'is-invalid' : ''
                      }`}
                      disabled={isSubmitting}
                    />
                    {fieldErrors.details && (
                      <span className="form-text is-invalid">
                        {fieldErrors.details}
                      </span>
                    )}
                  </div>
                )}

                {showAddressFields && (
                  <>
                    <p className="login-help-helper">
                      To help us find your account, please provide:
                    </p>

                    <div className="form-group">
                      <label
                        htmlFor="login-help-postcode"
                        className="form-label"
                      >
                        Postcode
                      </label>
                      <input
                        type="text"
                        id="login-help-postcode"
                        name="postcode"
                        value={form.postcode}
                        onChange={handleChange}
                        className={`form-control ${
                          fieldErrors.postcode ? 'is-invalid' : ''
                        }`}
                        autoComplete="postal-code"
                        disabled={isSubmitting}
                      />
                      {fieldErrors.postcode && (
                        <span className="form-text is-invalid">
                          {fieldErrors.postcode}
                        </span>
                      )}
                    </div>

                    <div className="form-group">
                      <label
                        htmlFor="login-help-house"
                        className="form-label"
                      >
                        House number or name
                      </label>
                      <input
                        type="text"
                        id="login-help-house"
                        name="house_number_or_name"
                        value={form.house_number_or_name}
                        onChange={handleChange}
                        className={`form-control ${
                          fieldErrors.house_number_or_name ? 'is-invalid' : ''
                        }`}
                        disabled={isSubmitting}
                      />
                      {fieldErrors.house_number_or_name && (
                        <span className="form-text is-invalid">
                          {fieldErrors.house_number_or_name}
                        </span>
                      )}
                    </div>
                  </>
                )}

                {showPasswordReset && (
                  <div className="login-help-reset">
                    <p>
                      You can reset your password here:{' '}
                      <Link to="/forgot-password" onClick={onClose}>
                        Reset password
                      </Link>
                    </p>
                  </div>
                )}

                {!showPasswordReset && (
                  <div className="modal-footer login-help-footer">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Submitting…' : 'Submit'}
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginHelpModal
